import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { ActivateAccountPage } from "../features/auth/ActivateAccountPage";
import { LoginPage } from "../features/auth/LoginPage";
import {
  ForgotPasswordPage,
  ResetPasswordPage,
} from "../features/auth/PasswordResetPages";
import { CalendarsPage } from "../features/calendars/CalendarsPage";
import { EmployeesPage } from "../features/employees/EmployeesPage";
import { HomePage } from "../features/HomePage";
import { MeetingDetailPage } from "../features/meetings/MeetingDetailPage";
import { ProjectDetailPage } from "../features/projects/ProjectDetailPage";
import { ProjectsPage } from "../features/projects/ProjectsPage";
import { ReleaseCalendarPage } from "../features/releases/ReleaseCalendarPage";
import { ReleaseDetailPage } from "../features/releases/ReleaseDetailPage";
import { ReleaseTemplatesPage } from "../features/releasetemplates/ReleaseTemplatesPage";
import { EnvironmentsPage } from "../features/environments/EnvironmentsPage";
import { ModuleTypesPage } from "../features/modules/ModuleTypesPage";
import { ReleaseSummaryReport } from "../features/releases/ReleaseSummaryReport";
import { ModulesPage } from "../features/modules/ModulesPage";
import { ReleaseFieldsPage } from "../features/releasefields/ReleaseFieldsPage";
import { UserGuidePage } from "../features/guide/UserGuidePage";
import { RolesPage } from "../features/roles/RolesPage";
import { CurrentProjectProvider } from "../shared/hooks/currentProject";
import { LeavePage } from "../features/resources/LeavePage";
import { NotificationsPage } from "../features/notifications/NotificationsPage";
import { PageTitleProvider } from "./PageTitleContext";
import { WatchlistPage } from "../features/watchlist/WatchlistPage";
import { ResourcePlannerPage } from "../features/resources/ResourcePlannerPage";
import { BacklogPage } from "../features/sprints/BacklogPage";
import { SprintDetailPage } from "../features/sprints/SprintDetailPage";
import { SprintsPage } from "../features/sprints/SprintsPage";
import { BugsPage } from "../features/bugs/BugsPage";
import { MyWorkPage } from "../features/workitems/MyWorkPage";
import { SearchPage } from "../features/workitems/SearchPage";
import { WorkItemDetailPage } from "../features/workitems/WorkItemDetailPage";
import { ChangeAnalyticsPage } from "../features/change/ChangeAnalyticsPage";
import { ReportsPage } from "../features/reports/ReportsPage";
import { ApprovalQueuePage } from "../features/timesheets/ApprovalQueuePage";
import { TimesheetPage } from "../features/timesheets/TimesheetPage";
import { TeamDetailPage } from "../features/teams/TeamDetailPage";
import { TeamsPage } from "../features/teams/TeamsPage";
import { AppShell } from "../layouts/AppShell";
import { ApiError } from "../shared/api/client";
import { LoadingState, PermissionDenied } from "../shared/ui";
import { AuthProvider, useAuth } from "./AuthContext";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Retrying a 401 or 403 is pointless — the client refreshes tokens on its own, and a
      // permission failure will fail identically however many times it is repeated.
      retry: (failureCount, error) => {
        if (
          error instanceof ApiError &&
          error.status >= 400 &&
          error.status < 500
        ) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

/** Sends anonymous visitors to sign in, remembering where they were going. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    // The session is being recovered from the refresh cookie. Rendering the login form here would
    // flash it at people who are, in fact, signed in.
    return (
      <div className="grid min-h-screen place-items-center bg-ink-50">
        <LoadingState label="Restoring your session…" />
      </div>
    );
  }

  if (status === "anonymous") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/** Hides a screen the reader's permissions do not cover, without pretending it does not exist. */
function RequirePermission({
  permission,
  what,
  children,
}: {
  permission: string;
  what: string;
  children: ReactNode;
}) {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : <PermissionDenied what={what} />;
}

function AuthenticatedRoutes() {
  return (
    /* Outside AppShell, because the shell's create button reads what the routes below publish,
       and so does the title in its top bar. */
    <CurrentProjectProvider>
      <PageTitleProvider>
        <AppShell>
        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* No permission: everybody may read their own notifications, and the query has no
              parameter with which to ask for anybody else's. */}
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/watching" element={<WatchlistPage />} />
          <Route
            path="/my-work"
            element={
              <RequirePermission permission="WORK_ITEM_VIEW" what="work items">
                <MyWorkPage />
              </RequirePermission>
            }
          />
          <Route
            path="/bugs"
            element={
              <RequirePermission permission="WORK_ITEM_VIEW" what="work items">
                <BugsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/search"
            element={
              <RequirePermission permission="WORK_ITEM_VIEW" what="work items">
                <SearchPage />
              </RequirePermission>
            }
          />
          <Route
            path="/work-items/:workItemId"
            element={
              <RequirePermission permission="WORK_ITEM_VIEW" what="work items">
                <WorkItemDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/backlog"
            element={
              <RequirePermission permission="WORK_ITEM_VIEW" what="the backlog">
                <BacklogPage />
              </RequirePermission>
            }
          />
          <Route
            path="/release-calendar"
            element={
              <RequirePermission permission="RELEASE_VIEW" what="the release calendar">
                <ReleaseCalendarPage />
              </RequirePermission>
            }
          />
          <Route
            path="/sprints"
            element={
              <RequirePermission permission="SPRINT_VIEW" what="sprints">
                <SprintsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/sprints/:sprintId"
            element={
              <RequirePermission permission="SPRINT_VIEW" what="sprints">
                <SprintDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/resources"
            element={
              <RequirePermission
                permission="RESOURCE_VIEW"
                what="resource planning"
              >
                <ResourcePlannerPage />
              </RequirePermission>
            }
          />
          <Route
            path="/leave"
            element={
              <RequirePermission permission="LEAVE_VIEW" what="leave">
                <LeavePage />
              </RequirePermission>
            }
          />
          <Route
            path="/timesheets"
            element={
              <RequirePermission permission="TIMESHEET_VIEW" what="timesheets">
                <TimesheetPage />
              </RequirePermission>
            }
          />
          <Route
            path="/timesheets/approvals"
            element={
              <RequirePermission
                permission="TIMESHEET_APPROVE"
                what="timesheet approvals"
              >
                <ApprovalQueuePage />
              </RequirePermission>
            }
          />
          <Route
            path="/timesheets/:timesheetId"
            element={
              <RequirePermission permission="TIMESHEET_VIEW" what="timesheets">
                <TimesheetPage />
              </RequirePermission>
            }
          />
          <Route
            path="/reports"
            element={
              <RequirePermission
                permission="REPORT_RESOURCE_VIEW"
                what="reports"
              >
                <ReportsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/schedule-changes"
            element={
              <RequirePermission
                permission="CHANGE_ANALYTICS_VIEW"
                what="schedule changes"
              >
                <ChangeAnalyticsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/projects"
            element={
              <RequirePermission permission="PROJECT_VIEW" what="projects">
                <ProjectsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/projects/:projectId"
            element={
              <RequirePermission permission="PROJECT_VIEW" what="projects">
                <ProjectDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/projects/:projectId/meetings/:meetingId"
            element={
              <RequirePermission permission="PROJECT_VIEW" what="projects">
                <MeetingDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/releases/:releaseId"
            element={
              <RequirePermission permission="RELEASE_VIEW" what="releases">
                <ReleaseDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/teams"
            element={
              <RequirePermission permission="TEAM_VIEW" what="teams">
                <TeamsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/teams/:teamId"
            element={
              <RequirePermission permission="TEAM_VIEW" what="teams">
                <TeamDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/employees"
            element={
              <RequirePermission permission="EMPLOYEE_VIEW" what="employees">
                <EmployeesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <RequirePermission
                permission="EMPLOYEE_VIEW"
                what="roles and permissions"
              >
                <RolesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/release-templates"
            element={
              <RequirePermission
                permission="RELEASE_TEMPLATE_VIEW"
                what="release templates"
              >
                <ReleaseTemplatesPage />
              </RequirePermission>
            }
          />
          {/* No permission gate: everyone who can sign in can read the guide. */}
          <Route path="/guide" element={<UserGuidePage />} />
          <Route
            path="/admin/release-fields"
            element={
              <RequirePermission permission="RELEASE_VIEW" what="release fields">
                <ReleaseFieldsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/environments"
            element={
              <RequirePermission permission="RELEASE_VIEW" what="environments">
                <EnvironmentsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/modules"
            element={
              <RequirePermission permission="RELEASE_VIEW" what="modules">
                <ModulesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/releases/:releaseId/report"
            element={
              <RequirePermission permission="RELEASE_VIEW" what="this release">
                <ReleaseSummaryReport />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/module-types"
            element={
              <RequirePermission permission="RELEASE_VIEW" what="module types">
                <ModuleTypesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/calendars"
            element={
              <RequirePermission permission="CALENDAR_VIEW" what="calendars">
                <CalendarsPage />
              </RequirePermission>
            }
          />
          {/* Anything else lands on Home rather than a dead end. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </AppShell>
      </PageTitleProvider>
    </CurrentProjectProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/*
        The sub-path the build was made for, so a deployment under /taskmanagement matches its
        routes. Vite fills BASE_URL in from `base`, which keeps the router and the asset paths from
        ever disagreeing — the trailing slash it carries is stripped because React Router wants a
        basename without one. Left at the root this is "", which is what it was before.
      */}
      <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, "")}>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/activate" element={<ActivateAccountPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="*"
              element={
                <RequireAuth>
                  <AuthenticatedRoutes />
                </RequireAuth>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
